import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { retry, tap, timeout } from 'rxjs/operators';
import { ContactUs } from '../../enums/contact-us.enum';
import { Identifier } from '../../enums/indetifier.enum';
import { BusinessAddress } from '../../interfaces/business-address.interface';
import {
  Logo, LogoType,
  StoreConfiguration,
  Variables
} from '../../interfaces/store-configuration.interface';
import { EnvironmentService } from '../environment/environment.service';
import { StoreConfigurationApiService } from './store-configuration-api.service';

@Injectable({
  providedIn: 'root'
})
export class StoreConfigurationRepositoryService {
  private environmentService = inject(EnvironmentService);
  private storeConfigurationApiService = inject(StoreConfigurationApiService);

  constructor() {
    // Auto-initialize when service is injected
    this.initializeStoreConfiguration();
  }

  private initializeStoreConfiguration(): void {
    // Auto-load store configuration when service is created
    this.load().catch((error) => {
      // Set a default configuration to prevent null issues
      this._storeConfigurations.set({
        id: '',
        company: { id: '', name: '' },
        sales_channel_code: '',
        application_configuration: {},
        sales_channel_pages: {},
        header_configurations: {},
        footer_configurations: [],
        logos: [],
        color_configurations: {},
        font_configurations: {},
        domain: '',
        is_cashpoints_offer_active: false,
        show_out_of_stock_products: true,
        is_bidding_active: false,
        show_gpt_widget: false,
        contact_mobile_number: '',
        contact_email: '',
        show_pincode_check: false,
        home_page: { published_page_contents: [] }
      } as any);
    });
  }



  private _storeConfigurations = signal<StoreConfiguration | null>(null);
  readonly storeConfigurations = this._storeConfigurations.asReadonly();

  // Cache to avoid repeated API calls
  private _isLoading = signal<boolean>(false);
  private _hasLoaded = signal<boolean>(false);
  private _loadPromise: Promise<any> | null = null;

  // Computed signals for derived state
  readonly storeConfigurationsNonNull = computed(() => {
    const config = this._storeConfigurations();
    return config !== null ? config : null;
  });

  readonly headerConfigurations = computed(() => this.storeConfigurationsNonNull()?.header_configurations);

  readonly showWarrantyRegistration = computed(() => this.storeConfigurationsNonNull()?.header_configurations?.is_warranty_registration_required);

  readonly showEstimatedDelivery = computed(() => this.storeConfigurationsNonNull()?.show_pincode_check);

  readonly footerConfigurations = computed(() => this.storeConfigurationsNonNull()?.footer_configurations);

  readonly siteMapUrl = computed(() => this.storeConfigurationsNonNull()?.site_map);

  readonly productComplaint = computed(() => {
    const footer_configurations = this.footerConfigurations();
    if (!footer_configurations) return null;

    const contactUsSection = footer_configurations.find((section: any) => section.identifier === Identifier.CONTACT_US);
    const productComplaints = contactUsSection?.sub_sections?.find((section: any) => section.title === ContactUs.ProductComplaints);

    if (productComplaints?.description) {
      try {
        return JSON.parse(productComplaints.description);
      } catch {
        return null;
      }
    }
    return null;
  });

  readonly dealerEnquiry = computed(() => {
    const footer_configurations = this.footerConfigurations();
    if (!footer_configurations) return null;

    const contactUsSection = footer_configurations.find((section: any) => section.identifier === Identifier.CONTACT_US);
    const dealerEnquiries = contactUsSection?.sub_sections?.find((section: any) => section.title === ContactUs.DealerEnquiries);

    if (dealerEnquiries?.description) {
      try {
        return JSON.parse(dealerEnquiries.description);
      } catch {
        return null;
      }
    }
    return null;
  });

  readonly orderEnquiry = computed(() => {
    const footer_configurations = this.footerConfigurations();
    if (!footer_configurations) return null;

    const contactUsSection = footer_configurations.find((section: any) => section.identifier === Identifier.CONTACT_US);
    const orderEnquiries = contactUsSection?.sub_sections?.find((section: any) => section.title === ContactUs.OrderEnquiries);

    if (orderEnquiries?.description) {
      try {
        return JSON.parse(orderEnquiries.description);
      } catch {
        return null;
      }
    }
    return null;
  });

  readonly bulkDiscountEnquiry = computed(() => {
    const footer_configurations = this.footerConfigurations();
    if (!footer_configurations) return null;

    const contactUsSection = footer_configurations.find((section: any) => section.identifier === Identifier.CONTACT_US);
    const bulkDiscountEnquiries = contactUsSection?.sub_sections?.find((section: any) => section.title === ContactUs.BulkDiscountEnquiries);

    if (bulkDiscountEnquiries?.description) {
      try {
        return JSON.parse(bulkDiscountEnquiries.description);
      } catch {
        return null;
      }
    }
    return null;
  });

  readonly rectangularLogo = computed(() => {
    const config = this.storeConfigurationsNonNull();
    const rectangularLogo = config?.logos?.find((logo: Logo) => logo?.format === LogoType.Rectangular);
    return rectangularLogo || null;
  });

  readonly rectangularLogoUrl = computed(() => {
    const logo = this.rectangularLogo();
    return logo?.url || '';
  });

  readonly colorConfigurations = computed(() => this.storeConfigurationsNonNull()?.color_configurations);

  readonly bodyConfigurations = computed(() => this.storeConfigurationsNonNull()?.body_configurations);

  readonly variables = computed(() => {
    const webstore = this.storeConfigurationsNonNull();
    if (!webstore) return null;

    const { company: { name = '' }, contact_mobile_number = '', contact_email = '', business_hours = '', gst_number = '' } = webstore;
    const company_address = this.getBusinessAddress(webstore);

    return {
      company_name: name,
      toll_free_number: contact_mobile_number,
      support_email_id: contact_email,
      business_hours,
      company_gstin: gst_number,
      company_address: company_address ? this.getBusinessAddressPreview(company_address) : ''
    };
  });

  // Convert getter methods to computed signals
  readonly faviconPath = computed(() => this._storeConfigurations()?.header_configurations?.favicon || '');

  readonly homePageTitle = computed(() => {
    const config = this._storeConfigurations();
    return config?.header_configurations?.title || '';
  });

  readonly companyName = computed(() => {
    const config = this._storeConfigurations();
    const { display_name = '', company: { name = '' } = {} } = config || {};
    return display_name ?? name;
  });

  readonly seoConfiguration = computed(() => {
    const config = this._storeConfigurations();
    const { meta_title = '', meta_description = '', search_keywords = [] } = config?.header_configurations || {};
    return ({
      meta_title, meta_description, keywords: search_keywords
    });
  });

  readonly salesChannelId = computed(() => this._storeConfigurations()?.id ?? '');

  readonly colorConfigurationsValue = computed(() => {
    const config = this._storeConfigurations();
    return config?.color_configurations ?? null;
  });

  readonly fontConfigurations = computed(() => {
    const config = this._storeConfigurations();
    const fontConfig = config?.font_configurations ?? null;


    return fontConfig;
  });

  readonly domain = computed(() => {
    const config = this._storeConfigurations();
    return config?.domain || '';
  });

  readonly isCashPointEnabled = computed(() => {
    const config = this._storeConfigurations();
    return config?.is_cashpoints_offer_active || false;
  });

  readonly showOutOfStockProducts = computed(() => {
    const config = this._storeConfigurations();
    return config?.show_out_of_stock_products ?? true;
  });

  readonly homePage = computed(() => {
    const config = this._storeConfigurations();
    return config?.home_page?.published_page_contents;
  });

  readonly categoryPageConfiguration = computed(() => {
    const config = this._storeConfigurations();
    const result = config?.sales_channel_pages?.category;
    return result;
  });

  readonly searchPageConfiguration = computed(() => {
    const config = this._storeConfigurations();
    const result = config?.sales_channel_pages?.search;
    return result;
  });

  readonly productsPageConfiguration = computed(() => {
    const config = this._storeConfigurations();
    const result = config?.sales_channel_pages?.products;
    return result;
  });

  readonly productDetailsPageConfiguration = computed(() => {
    const config = this._storeConfigurations();
    return config?.sales_channel_pages?.product_details;
  });

  readonly showProductBidding = computed(() => {
    const config = this._storeConfigurations();
    return config?.is_bidding_active ?? false;
  });

  readonly showGptWidgets = computed(() => {
    const config = this._storeConfigurations();
    return config?.show_gpt_widget ?? false;
  });

  readonly storeContactDetails = computed(() => {
    const config = this._storeConfigurations();
    return {
      contact_mobile_number: config?.contact_mobile_number || '',
      contact_email: config?.contact_email || ''
    };
  });

  readonly razorpayConfiguration = computed(() => {
    const config = this._storeConfigurations();
    return config?.razorpay_configurations ?? null;
  });

  // Convert Observable method to computed signal
  readonly policiesSubsections = computed(() => {
    const config = this._storeConfigurations();
    if (!config?.footer_configurations) return [];

    const footer_configurations = config.footer_configurations;
    if (Array.isArray(footer_configurations)) {
      // Only get sub_sections from the POLICIES section
      const policiesSection = footer_configurations.find(
        (section: any) => section.identifier === Identifier.POLICIES
      );
      return policiesSection?.sub_sections || [];
    }
    return [];
  });

  getStoreConfiguration() {
    return this.storeConfigurationApiService.getStoreConfiguration().pipe(
      tap(storeConfigurations => {

        if (storeConfigurations) {
          // prepare environment variables
          const { application_configuration, sales_channel_code, company: { id = '' } = {} } = storeConfigurations as any || {};

          const envData = {
            ...application_configuration,
            sales_channel_code: sales_channel_code || '',
            sellerId: id,
            url: `${this.environmentService.baseUrl}/consumer/sellers/${id}`,
            url_2: `${this.environmentService.backendBaseUrl}/v2/consumer/sellers/${id}`,
            product_url: `${this.environmentService.baseUrl}/consumer`,
            product_url_2: `${this.environmentService.backendBaseUrl}/v2/consumer`,
            client_id: application_configuration?.client_id || '',
            client_secret: application_configuration?.client_secret || ''
          };

          this.environmentService.updateEnvironment(envData);
          this._storeConfigurations.set(storeConfigurations);
        }
      })
    );
  }

  load(): Promise<any> {
    // Return cached promise if already loading
    if (this._loadPromise) {
      return this._loadPromise;
    }

    // Return immediately if already loaded
    if (this._hasLoaded()) {
      return Promise.resolve(this._storeConfigurations());
    }

    // Set loading state
    this._isLoading.set(true);

    try {
      this._loadPromise = firstValueFrom(
        this.getStoreConfiguration().pipe(
          retry(1),
          timeout(30000) // Add 30 second timeout
        )
      )
        .then((result) => {
          this._hasLoaded.set(true);
          this._isLoading.set(false);
          this._loadPromise = null; // Clear the promise cache
          return result;
        })
        .catch(() => {
          this._isLoading.set(false);
          this._loadPromise = null; // Clear the promise cache
          return Promise.resolve();
        });

      return this._loadPromise;
    } catch (error) {
      this._isLoading.set(false);
      this._loadPromise = null;
      return Promise.resolve();
    }
  }

  // Add method to check if store configuration is ready
  readonly isStoreConfigurationReady = computed(() => {
    return !!this._storeConfigurations();
  });

  readonly isLoading = this._isLoading.asReadonly();
  readonly hasLoaded = this._hasLoaded.asReadonly();

  // Add method to get store configuration with timeout
  async waitForStoreConfiguration(timeoutMs: number = 30000): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      if (this._storeConfigurations()) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return false;
  }

  parseVariables(textToParse = '', variables: Variables | null) {
    if (textToParse && variables) {
      Object.keys(variables).forEach(key => {
        const value = (variables as any)[key];
        if (value) {
          textToParse = textToParse?.split(`{{${key}}}`).join(value);
        }
      });
    }
    return textToParse;
  }

  setStoreConfigurations(storeConfigurations: StoreConfiguration | null) {
    this._storeConfigurations.set(storeConfigurations);
  }

  private getBusinessAddress(webstore: StoreConfiguration): BusinessAddress | null {
    const { address_legal_name = '',
      address_line_1 = '',
      address_line_2 = '',
      landmark = '',
      city = '',
      state: { name = '' } = {},
      pincode = '' } = webstore || {};

    if (address_legal_name) {
      return ({
        address_legal_name,
        address_line_1,
        address_line_2,
        landmark,
        city,
        state: name as any,
        pincode
      });
    } else {
      return null;
    }
  }

  private getBusinessAddressPreview(businessAddress: BusinessAddress): string {
    if (businessAddress) {
      return Object.keys(businessAddress)
        .filter(key => (businessAddress as any)[key])
        .map(key => (businessAddress as any)[key])
        .join(', ');
    }
    return '';
  }
}
export function storeConfigurationRepositoryServiceFactory(storeConfigurationRepositoryService:
  StoreConfigurationRepositoryService) {
  return () => {
    return storeConfigurationRepositoryService.load();
  };
}
